/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  Input,
  OnDestroy,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';

import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import {
  combineLatest,
  interval,
  map,
  Observable,
  shareReplay,
  Subject,
  Subscription,
} from 'rxjs';

import { CommonModule } from '@angular/common';
import { Params, RouterModule } from '@angular/router';
import { Product } from '@app/akita/api/products/models/product.model';
import { PopularDevices } from '@app/akita/api/remote-config/models/popular-devices.model';
import { BestSellerCardComponent } from './components/best-seller-card/best-seller-card.component';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  generateFiltersID,
  PRODUCT_SORTING_RANKED_BY_SALES,
  ProductSearchFilters,
  ProductSearchSortMode,
} from '@app/akita/api/products/models/product-search.filters.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import { TimerComponents } from '@app/akita/api/featured/models/featured.state';
import { AkitaFeaturedQuery } from '@app/akita/api/featured/state/featured.query';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { PopsyScrollComponent } from '../../../shared/components/popsy-scroller/popsy-scroller.component';
import { AkitaStartingPricesService } from '@app/akita/api/starting-prices/state/starting-prices.service';
import { StartingPriceModel } from '@app/shared/models/api/starting-price.model';
import { CheapestSellerCardComponent } from './components/cheapest-seller-card/cheapest-seller-card.component';

const CAT_ORDER = ['SMARTPHONE', 'TABLET', 'LAPTOP', 'WATCH'];

const FIREBASE_BANNER_FOLDER =
  'https://firebasestorage.googleapis.com/v0/b/popsygae.appspot.com/o/HomePageBanners%2FBestDealsBanner%2F';
const FIREBASE_BANNER_FOLDER_2 =
  '_v2.png?alt=media&token=829995d9-4e62-4320-96f9-c76105a876b9';

@Component({
  selector: 'app-best-sellers-picker',
  templateUrl: './best-sellers-picker.template.html',
  styleUrls: ['./best-sellers-picker.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    BestSellerCardComponent,
    PopsyScrollComponent,
    CheapestSellerCardComponent,
  ],
})
export class BestSellersPickerComponent implements OnDestroy {
  @Input() public hideImageBanner?: boolean = false;
  public readonly biggerThan$: Observable<ScreenSizeInfoState>;

  public readonly language$: Observable<string>;

  public popularDevices$: Observable<PopularDevices | null> = new Observable();
  public bestSellers: Array<Product | null> = new Array(0);

  public queryParamsToKeep$: Observable<Params>;

  public bestSellersApi: Signal<Array<Product> | null>;
  public filteredBrand: WritableSignal<string | null> = signal('Apple');

  public filteredCategory: WritableSignal<string | null> = signal('SMARTPHONE');
  public bestSellersPerCategory: WritableSignal<Array<StartingPriceModel>> = signal([]);

  public bannerLink: Signal<string | null>;

  public countDownTimer: Signal<TimerComponents>;

  public productCategories: Signal<Array<string>> = signal([
    'SMARTPHONE',
    'LAPTOP',
    'TABLET',
    'ACCESSORIES',
  ]);

  public readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly translationService: TranslocoService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaFeaturedQuery: AkitaFeaturedQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaStartingPricesService: AkitaStartingPricesService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.language$ = this.translationService.langChanges$;
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.countDownTimer = toSignal(
      interval(1000).pipe(
        map(() => this.akitaFeaturedQuery.getTimerCountDown()),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
      { initialValue: this.akitaFeaturedQuery.getTimerCountDown() }
    );

    const bestSellersFilter = {
      ...new ProductSearchFilters(),
      country: this.akitaProductsQuery.country,
      language: this.akitaProductsQuery.language,
      sorting: PRODUCT_SORTING_RANKED_BY_SALES as ProductSearchSortMode,
      limit: 7,
    };

    const bestSellersAppleiPhone = {
      ...new ProductSearchFilters(),
      country: this.akitaProductsQuery.country,
      language: this.akitaProductsQuery.language,
      sorting: PRODUCT_SORTING_RANKED_BY_SALES as ProductSearchSortMode,
      category: ['SMARTPHONE'],
      brand: ['Apple'],
      limit: 7,
    };

    const bestSellersSamsungPhone = {
      ...new ProductSearchFilters(),
      country: this.akitaProductsQuery.country,
      language: this.akitaProductsQuery.language,
      sorting: PRODUCT_SORTING_RANKED_BY_SALES as ProductSearchSortMode,
      category: ['SMARTPHONE'],
      brand: ['Samsung'],
      limit: 7,
    };

    this.subscriptions.add(
      this.akitaStartingPricesService.search(bestSellersFilter).subscribe({
        next: () => {},
        error: () => {},
      })
    );

    this.subscriptions.add(
      this.akitaStartingPricesService.search(bestSellersAppleiPhone).subscribe({
        next: () => {},
        error: () => {},
      })
    );

    this.subscriptions.add(
      this.akitaStartingPricesService.search(bestSellersSamsungPhone).subscribe({
        next: () => {},
        error: () => {},
      })
    );

    this.bestSellersApi = toSignal(
      combineLatest([
        this.akitaProductsQuery.selectSearch(generateFiltersID(bestSellersFilter)),
        this.akitaProductsQuery.selectSearch(generateFiltersID(bestSellersAppleiPhone)),
        this.akitaProductsQuery.selectSearch(generateFiltersID(bestSellersSamsungPhone)),
      ]).pipe(
        map(([bestSellers, bestSellersApple, bestSellersSamsung]) => {
          const results = bestSellers
            .filter((product) => CAT_ORDER.includes(product.category)) // Filter out products with categories not in the list
            .sort((a, b) => CAT_ORDER.indexOf(a.category) - CAT_ORDER.indexOf(b.category))
            .reduce((acc: any, product) => {
              // Check if this category has already been added
              if (!acc.some((p: any) => p.category === product.category)) {
                acc.push(product); // Add the first product of each category
              }
              return acc;
            }, []);

          results.push(bestSellersApple[1]);
          results.push(bestSellersSamsung[0]);

          return results;
        })
      ),
      {
        initialValue: null,
      }
    );

    // Filter the best sellers by category
    effect(
      () => {
        if (this.filteredCategory() !== null) {
          const categories = new Array<string>();
          categories.push(this.filteredCategory() || '');

          this.subscriptions.add(
            this.akitaStartingPricesService
              .search({
                ...bestSellersFilter,
                category: categories,
              })
              .subscribe({
                next: (result) =>
                  this.bestSellersPerCategory.set(
                    result.filter((x) => x.category === this.filteredCategory())
                  ),
                error: () => {},
              })
          );
        }
      },
      { allowSignalWrites: true }
    );

    this.bannerLink = toSignal(
      this.biggerThan$.pipe(
        map((biggerThan) => {
          const size = biggerThan.sm ? 'large' : 'big';
          return `${FIREBASE_BANNER_FOLDER}${size}${FIREBASE_BANNER_FOLDER_2}`;
        })
      ),
      { initialValue: null }
    );
  }

  public switchCategory(category: string): void {
    if (this.filteredCategory() === category) {
      this.filteredCategory.set(null);
    } else {
      this.filteredCategory.set(category);
    }
  }

  public capitalizeCategory(str: string): string | null {
    return str
      .charAt(0)
      .toUpperCase()
      .concat(str.slice(1).replace(/ /g, '_').toLowerCase());
  }

  public getSortedBrands(
    result: Observable<ProductSearchFilterOptions>
  ): Observable<Array<string> | null> {
    return result.pipe(
      map((options) => {
        const results = options.brand;
        if (!options.brand.length) {
          return null;
        }

        const anyitems = [...results].filter((x) => x !== 'Apple' && x !== 'Samsung');

        // SHOW SAMSUNG SECOND
        [...results.filter((x) => x === 'Samsung')].reverse().forEach((item) => {
          anyitems.unshift(item);
        });

        // SHOW APPLE FIRST
        [...results.filter((x) => x === 'Apple')].reverse().forEach((item) => {
          anyitems.unshift(item);
        });

        return anyitems;
      })
    );
  }

  public informationPageUrlFragment(path: string): Array<string> {
    const fragment = this.akitaRouterQuery.homeUrlFragment;

    fragment.push(path);
    return fragment;
  }

  public trackByModel(index: number, category?: any | null): string {
    return (category as any)?.id || category || `${index}`;
  }

  public trackByKey(index: number, value?: any | null): string {
    return `${value || index}`;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
